import React from 'react';
import {isAxiosError} from 'axios';
import {useNavigate} from 'react-router-dom';
import {ConfirmationModal, ConfirmationSuccessIcon} from '@rocket-ui/components';
import {useCreatePaymentWay, useFetchPaymentWays} from '@rocket-ui/lk-api';
import {ROUTES} from 'app/router/routes';
import {
	SBP_PAYMENT_SUCCESS_DESCRIPTION,
	SBP_PAYMENT_SUCCESS_TITLE,
	ProfileAlertsBanner,
	SbpBankStep,
	SbpPhoneStep,
	SbpReviewStep,
	StepProgress,
	buildSbpPaymentWayRequest,
	extractSbpPhoneDigits,
	formatProfileRecipientShortName,
	isSbpPhoneValid,
	useProfile,
	type SelectedBank,
} from 'features/profile';
import {RegistrationButton, RegistrationConfirmationModal} from 'features/registration';
import styles from './AddAccountPaymentPage.module.css';

type SbpStep = 'phone' | 'bank' | 'review';

const STEPS: SbpStep[] = ['phone', 'bank', 'review'];

const backIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="1.6" />
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M20 12.9135L5 12.9135L5 11.1992L20 11.1992L20 12.9135Z"
			fill="currentColor"
		/>
	</svg>
);

const closeIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M3.99805 3.99805L20.0004 20.0004" stroke="currentColor" strokeWidth="1.6" />
		<path d="M3.99805 20L20.0004 3.99768" stroke="currentColor" strokeWidth="1.6" />
	</svg>
);

function getCreatePaymentWayErrorMessage(error: unknown): string {
	if (isAxiosError<{errors?: string[]; message?: string; detail?: string}>(error)) {
		return (
			error.response?.data?.errors?.[0] ||
			error.response?.data?.message ||
			error.response?.data?.detail ||
			error.message
		);
	}

	return error instanceof Error ? error.message : 'Не удалось добавить способ оплаты';
}

export function AddSbpPaymentPage(): React.ReactElement {
	const navigate = useNavigate();
	const {profile, isSuccess: isProfileLoaded} = useProfile();
	const {data: paymentWays = []} = useFetchPaymentWays();
	const {mutateAsync: createPaymentWay, isLoading: isCreating} = useCreatePaymentWay();

	const [step, setStep] = React.useState<SbpStep>('phone');
	const [phone, setPhone] = React.useState('');
	const [selectedBank, setSelectedBank] = React.useState<SelectedBank | null>(null);
	const [isPrimary, setIsPrimary] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);
	const [isExitConfirmOpen, setIsExitConfirmOpen] = React.useState(false);
	const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);
	const didPrefillPhone = React.useRef(false);

	const hasExistingPaymentWays = paymentWays.some((way) => way.visible !== false);
	const showPrimaryToggle = hasExistingPaymentWays;
	const recipient = formatProfileRecipientShortName(profile) || '—';

	React.useEffect(() => {
		if (didPrefillPhone.current || !isProfileLoaded) {
			return;
		}

		didPrefillPhone.current = true;

		if (profile.phone && profile.phone !== '—') {
			setPhone(extractSbpPhoneDigits(profile.phone));
		}
	}, [isProfileLoaded, profile.phone]);

	React.useEffect(() => {
		if (!hasExistingPaymentWays) {
			setIsPrimary(true);
		}
	}, [hasExistingPaymentWays]);

	const stepIndex = STEPS.indexOf(step);
	const showBack = step !== 'phone';
	const canContinue =
		!isCreating &&
		!isSuccessOpen &&
		((step === 'phone' && isSbpPhoneValid(phone)) ||
			(step === 'bank' && selectedBank !== null) ||
			step === 'review');

	const handleCloseRequest = () => {
		if (isCreating || isSuccessOpen) {
			return;
		}

		setIsExitConfirmOpen(true);
	};

	const handleConfirmExit = () => {
		setIsExitConfirmOpen(false);
		navigate(ROUTES.profile);
	};

	const handleSuccessContinue = () => {
		setIsSuccessOpen(false);
		navigate(ROUTES.profile);
	};

	const handleBack = () => {
		if (isCreating || isSuccessOpen) {
			return;
		}

		if (step === 'review') {
			setStep('bank');
			setSubmitError(null);
			return;
		}

		if (step === 'bank') {
			setStep('phone');
		}
	};

	const handleBankSelect = (bank: SelectedBank) => {
		setSelectedBank(bank);
		setSubmitError(null);
		setStep('review');
	};

	const handleContinue = async () => {
		if (!canContinue) {
			return;
		}

		if (step === 'phone') {
			setStep('bank');
			return;
		}

		if (step === 'bank') {
			if (!selectedBank) {
				return;
			}

			setStep('review');
			return;
		}

		if (isCreating) {
			return;
		}

		const request = buildSbpPaymentWayRequest({
			phone,
			selectedBank,
			isPrimary: hasExistingPaymentWays ? isPrimary : true,
		});

		if (!request) {
			setSubmitError('Проверьте номер телефона и банк');
			return;
		}

		try {
			await createPaymentWay(request);
			setIsSuccessOpen(true);
		} catch (error) {
			setSubmitError(getCreatePaymentWayErrorMessage(error));
		}
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					{showBack ? (
						<button type="button" className={styles.backButton} onClick={handleBack} aria-label="Назад">
							{backIcon}
						</button>
					) : (
						<span className={styles.headerSpacer} aria-hidden />
					)}
					<StepProgress currentStep={stepIndex} totalSteps={STEPS.length} className={styles.progress} />
					<button
						type="button"
						className={styles.closeButton}
						onClick={handleCloseRequest}
						aria-label="Закрыть"
					>
						{closeIcon}
					</button>
				</div>
			</header>

			<div className={styles.content}>
				<div className={`${styles.contentInner}${step === 'bank' || step === 'review' ? ` ${styles.contentInnerWhite}` : ''}`}>
					<ProfileAlertsBanner />
					{step === 'phone' ? <SbpPhoneStep value={phone} onChange={setPhone} /> : null}
					{step === 'bank' ? <SbpBankStep selectedBank={selectedBank} onSelect={handleBankSelect} /> : null}
					{step === 'review' ? (
						<>
							<SbpReviewStep
								recipient={recipient}
								phone={phone}
								bank={selectedBank}
								isPrimary={isPrimary}
								showPrimaryToggle={showPrimaryToggle}
								onPrimaryChange={setIsPrimary}
								error={submitError}
							/>
							<div className={styles.contentActions}>
								<RegistrationButton
									variant="primary"
									className={styles.continueButton}
									onClick={handleContinue}
									disabled={!canContinue}
									loading={isCreating}
								>
									Всё верно
								</RegistrationButton>
							</div>
						</>
					) : null}
				</div>
			</div>

			{step !== 'bank' && step !== 'review' ? (
				<footer className={styles.footer}>
					<div className={styles.footerInner}>
						<RegistrationButton
							variant="primary"
							className={styles.continueButton}
							onClick={handleContinue}
							disabled={!canContinue}
							loading={isCreating}
						>
							Продолжить
						</RegistrationButton>
					</div>
				</footer>
			) : null}

			<ConfirmationModal
				isOpen={isExitConfirmOpen}
				title="Хотите выйти?"
				description="Вы уверены, что хотите прервать добавление реквизитов?"
				confirmText="Выйти"
				onConfirm={handleConfirmExit}
				onCancel={() => setIsExitConfirmOpen(false)}
			/>

			<RegistrationConfirmationModal
				isOpen={isSuccessOpen}
				title={SBP_PAYMENT_SUCCESS_TITLE}
				description={SBP_PAYMENT_SUCCESS_DESCRIPTION}
				confirmText="Продолжить"
				illustration={<ConfirmationSuccessIcon />}
				onConfirm={handleSuccessContinue}
				onCancel={handleSuccessContinue}
			/>
		</div>
	);
}
